import * as tslib_1 from "tslib";
/** @jsx jsx */

import { jsx } from "@emotion/core";
import * as React from "react";
import { Layer } from "./Layer";
import { Positioner } from "./Positions";
import { arrowStyles } from "./Tooltip";
import { useFocusElement } from "./Hooks/focus";
import { animated } from "react-spring";
import { Sheet } from "./Sheet";
import { useMedia } from "use-media";
import PropTypes from "prop-types";
import { useTheme } from "./Theme/Providers";
var AnimatedLayer = animated(Layer);
export var Popover = function Popover(_a) {
  var content = _a.content,
      children = _a.children,
      placement = _a.placement,
      _b = _a.closeOnMenuItemClick,
      closeOnMenuItemClick = _b === void 0 ? true : _b,
      _c = _a.isOpen,
      defaultShow = _c === void 0 ? false : _c;
  var theme = useTheme();

  var _d = React.useState(defaultShow),
      show = _d[0],
      setShow = _d[1];

  var child = React.Children.only(children);
  var triggerRef = React.useRef(null);
  var popoverRef = React.useRef(null);
  var bind = useFocusElement(show, {
    escapeDeactivates: true,
    clickOutsideDeactivates: true
  }).bind;
  var bg = theme.colors.mode === "dark" ? theme.colors.background.tint1 : theme.colors.background.layer;

  function open() {
    setShow(true);
    document.body.addEventListener("click", onBodyClick, false);
    document.body.addEventListener("keydown", onBodyKeyDown, false);
  }

  React.useEffect(function () {
    return removeBodyListeners;
  }, []);

  function close() {
    setShow(false);
    removeBodyListeners();
  }

  function removeBodyListeners() {
    document.body.removeEventListener("click", onBodyClick, false);
    document.body.removeEventListener("keydown", onBodyKeyDown, false);
  }

  function onBodyClick(e) {
    var trigger = triggerRef.current;
    var popover = popoverRef.current;

    if (!trigger || !popover) {
      return;
    } // Don't close if clicking the trigger


    if (trigger === e.target || trigger.contains(e.target)) {
      return;
    } // or the popover, sometimes


    if (popover === e.target || popover.contains(e.target)) {
      if (closeOnMenuItemClick) {
        var target = e.target;

        if (target.hasAttribute('[data-trigger-close="true"]') || target.closest('[data-trigger-close="true"]')) {
          close();
          return;
        }
      }

      return;
    }

    close();
  }

  function onBodyKeyDown(e) {
    // close on escape key or enter
    if (e.keyCode === 27) {
      close();
    } // close on enter


    if (closeOnMenuItemClick && e.keyCode === 13) {
      close();
    }
  }

  function renderTrigger(_a) {
    var _ref = _a.ref;
    return React.cloneElement(child, {
      onClick: function onClick(e) {
        onTriggerClicked(e);

        if (child.props.onClick) {
          child.props.onClick(e);
        }
      },
      ref: function ref(el) {
        _ref(el);

        triggerRef.current = el;
      },
      role: "button",
      "aria-expanded": show,
      "aria-haspopup": true
    });
  }

  function onTriggerClicked(e) {
    return show ? close() : open();
  }

  return jsx(Positioner, {
    placement: placement,
    isOpen: show,
    target: renderTrigger
  }, function (_a, animation) {
    var placement = _a.placement,
        _ref2 = _a.ref,
        arrowProps = _a.arrowProps,
        style = _a.style;
    return jsx(AnimatedLayer, {
      role: "dialog",
      elevation: "md",
      ref: function ref(el) {
        _ref2(el);

        bind.ref.current = el;
        popoverRef.current = el;
      },
      style: tslib_1.__assign({}, style, {
        opacity: animation.opacity
      }),
      "data-placement": placement,
      css: {
        zIndex: theme.zIndices.popover,
        margin: theme.spaces.sm,
        borderRadius: theme.radii.md,
        background: bg
      }
    }, jsx("div", {
      "data-placement": placement,
      css: arrowStyles(bg),
      ref: arrowProps.ref,
      style: arrowProps.style
    }), content);
  });
};
Popover.propTypes = {
  isOpen: PropTypes.bool,
  children: PropTypes.node,
  content: PropTypes.node,
  closeOnMenuItemClick: PropTypes.bool,
  placement: PropTypes.oneOf(["auto-start", "auto", "auto-end", "top-start", "top", "top-end", "right-start", "right", "right-end", "bottom-end", "bottom", "bottom-start", "left-end", "left", "left-start"])
};
/**
 * Display popover contents in a bottom sheet if
 * on mobile devices. I generally find that this provides a
 * better use experience on smaller screens.
 */

export var ResponsivePopover = function ResponsivePopover(props) {
  var theme = useTheme(); // show popover on iPad+

  var showPopover = useMedia({
    minWidth: theme.breakpoints.sm
  });

  var _a = React.useState(false),
      isOpen = _a[0],
      setIsOpen = _a[1];

  if (showPopover) {
    return jsx(Popover, tslib_1.__assign({}, props));
  }

  return jsx(React.Fragment, null, React.cloneElement(props.children, {
    onClick: function onClick() {
      setIsOpen(true);
    }
  }), jsx(Sheet, {
    position: "bottom",
    isOpen: isOpen,
    onRequestClose: function onRequestClose() {
      return setIsOpen(false);
    }
  }, props.content));
};
ResponsivePopover.propTypes = tslib_1.__assign({}, Popover.propTypes);